import {useCallback, useEffect, useMemo, useRef, useState} from 'preact/hooks';

import {AuthorizeIframe} from '~/components/AuthorizeIframe';
import {BrandedButton} from '~/components/BrandedButton';
import {BrandedButtonIcon} from '~/components/BrandedButtonIcon';
import {SIGN_TOAST_KEY} from '~/constants/toast';
import {useAuthorizeState} from '~/foundation/AuthorizeState/hooks';
import {useBugsnag} from '~/foundation/Bugsnag/hooks';
import {useI18n} from '~/foundation/I18n/hooks';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {useRootProvider} from '~/foundation/RootProvider/hooks';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import {useAdjustedUxMode} from '~/hooks/useAdjustedUxMode';
import {useAuthorizeUrl} from '~/hooks/useAuthorizeUrl';
import {useCompleteLogin} from '~/hooks/useCompleteLogin';
import {useDebouncedCallback} from '~/hooks/useDebounce';
import {useElementEventListener} from '~/hooks/useElementEventListener';
import {useEmailListener} from '~/hooks/useEmailListener';
import {useWindoidMessageHandler} from '~/hooks/useWindoidMessageHandler';
import {useWindoidPreAuthPromptContinue} from '~/hooks/useWindoidPreAuthPromptContinue';
import type {AnalyticsContext} from '~/types/analytics';
import type {SdkErrorCode} from '~/types/authorize';
import type {
  AuthorizeErrorEvent,
  CompletedEvent,
  StartEvent,
} from '~/types/event';
import type {IframeElement} from '~/types/iframe';
import type {ToastData} from '~/types/toast';
import {isMobileBrowser} from '~/utils/browser';
import {isoDocument} from '~/utils/document';
import register from '~/utils/register';
import {displayToast, setToastInStorage} from '~/utils/toast';
import {openWindoid} from '~/utils/windoid';
import {isoWindow} from '~/utils/window';

import {getAnalyticsProps} from './monorail';
import type {
  ShopLoginButtonProps,
  ShopLoginButtonWebComponentProps,
} from './types';
import {hasAnyInitScript} from './util';

// Flows to show a same-page Toast on
const SAME_PAGE_TOAST_FLOWS: AnalyticsContext[] = ['loginWithShop'];

const ShopLoginButtonInternal = ({
  action,
  activatorId,
  analyticsContext = 'loginWithShop',
  anchorTo,
  autoOpen,
  bordered,
  email,
  emailInputSelector,
  experiments,
  firstname,
  fullWidth,
  hideButton,
  lastname,
  persistAfterSignIn = true,
  proxy = false,
  uxMode = 'iframe',
  version,
  ...props
}: ShopLoginButtonProps) => {
  const {dispatch} = useAuthorizeState();
  const {notify} = useBugsnag();
  const {trackPageImpression, trackUserAction} = useMonorail();
  const {translate} = useI18n();
  const [contentVisible, setContentVisible] = useState(true);
  const {instanceId} = useRootProvider();

  const activatorElementRef = useRef<HTMLElement | null>(
    activatorId ? isoDocument.getElementById(activatorId) : null,
  );
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const iframeRef = useRef<IframeElement | null>(null);
  const windoidRef = useRef<WindowProxy | null>(null);
  const [error, setError] = useState<SdkErrorCode>();

  const adjustedUxMode = useAdjustedUxMode({
    defaultUxMode: 'redirect',
    uxMode,
  });

  const {getSubmittedEmail, updateEmailToPost, updateNamesToPost} =
    useEmailListener({
      email,
      emailInputSelector,
      iframeRef,
    });

  useEffect(() => {
    updateNamesToPost(firstname, lastname);
  }, [firstname, lastname, updateNamesToPost]);

  const elementEventListeners = useMemo(() => {
    const start = async ({detail}: StartEvent) => {
      setError(undefined);
      const email = detail.email;
      await updateEmailToPost(email);
    };
    return {requestShow: start, start};
  }, [updateEmailToPost]);

  useElementEventListener(elementEventListeners);

  useEffect(() => {
    if (action) {
      if (action === 'follow') {
        // eslint-disable-next-line no-console
        console.warn?.(
          '[Sign in with Shop] The action prop is deprecated, please use <shop-follow-button> instead.',
        );
      } else {
        // eslint-disable-next-line no-warning-comments
        // TODO: We removed the bugsnag event since it was extremely noisy.
        // We can iterate on how to deal with invalid configs in https://github.com/Shopify/shop-identity/issues/3519.
        // eslint-disable-next-line no-console
        console.warn?.(
          `[Sign in with Shop] Received deprecated attribute: action, "${action}"`,
        );
      }
    }
  }, [action, notify]);

  useEffect(() => {
    if (!hideButton) {
      trackPageImpression({page: 'SIGN_IN_WITH_SHOP_BUTTON'});
    }
  }, [hideButton, trackPageImpression]);

  const windoidCompleteLogin = useCompleteLogin({
    storefrontOrigin: props.storefrontOrigin,
  });
  const handleComplete = useCallback(
    async (completedEvent: CompletedEvent) => {
      const {email, givenName, userInteracted} = completedEvent;
      if (!persistAfterSignIn) {
        setContentVisible(false);
      }

      let actionText;
      if (!userInteracted) {
        actionText = translate('shopLoginButton.toast.action') as string;
      }

      let text = translate('shopLoginButton.toast.default') as string;
      if (givenName) {
        text = translate('shopLoginButton.toast.user', {
          user: givenName,
        }) as string;
      }

      const toast: ToastData = {
        actionText,
        analyticsTraceId: instanceId,
        flow: props.flow,
        subText: email,
        text,
      };

      if (SAME_PAGE_TOAST_FLOWS.includes(analyticsContext)) {
        // display the toast immediately in some cases
        displayToast(toast);
      } else if (adjustedUxMode === 'windoid') {
        // only if windoid, set toast copy in storage so that it can be
        // retrieved and rendered at the next page load
        setToastInStorage(SIGN_TOAST_KEY, toast);
      }

      await windoidCompleteLogin(completedEvent);
    },
    [
      adjustedUxMode,
      analyticsContext,
      instanceId,
      persistAfterSignIn,
      props.flow,
      translate,
      windoidCompleteLogin,
    ],
  );

  // eslint-disable-next-line no-warning-comments
  // TODO: Remove this once the Installments components are ported to Preact
  // https://github.com/Shopify/shop-server/issues/82525
  const isPrequalFlow = analyticsContext === 'loginWithShopPrequal';
  const prequalProps = isPrequalFlow
    ? {
        disableSignUp: false,
        flow: 'prequal',
        flowVersion: 'unspecified',
        hideCopy: false,
        isCompactLayout: false,
        proxy: true,
      }
    : {};

  const shouldProxy = useMemo(() => {
    if (version !== undefined) {
      // eslint-disable-next-line no-warning-comments
      // TODO: We removed the DeprecatedAttributeError bugsnag event, since it was extremely noisy.
      // We can iterate on how to deal with invalid configs in https://github.com/Shopify/shop-identity/issues/3519.
      // eslint-disable-next-line no-console
      console.warn?.(
        `[Sign in with Shop] Received deprecated attribute: version, "${version}"`,
      );
    }

    return proxy || version === '2';
  }, [proxy, version]);

  const {authorizeUrl, getAuthorizeUrl} = useAuthorizeUrl({
    analyticsContext,
    error,
    experiments,
    ...props,
    proxy: shouldProxy,
    uxMode: adjustedUxMode,
    ...prequalProps,
  });

  const handleError = useCallback(
    ({code}: AuthorizeErrorEvent): void => {
      switch (code) {
        case 'server_error':
          displayToast({
            analyticsTraceId: instanceId,
            flow: props.flow,
            text: translate('shopLoginButton.subject_mapping_error') as string,
            variant: 'error',
          });
          break;
        case 'retriable_server_error':
          if (error === code) {
            iframeRef.current?.reload();
          }
          setError(code);
          break;
      }
    },
    [error, instanceId, props.flow, translate],
  );

  const handleWindoidMessage = useWindoidMessageHandler({
    handleClose: () => {
      dispatch({type: 'windoidClosed'});
    },
    handleComplete,
    handleError,
    handleOpen: () => {
      dispatch({type: 'windoidOpened'});
    },
    windoidRef,
  });

  const openWindoidOrRedirect = useCallback(
    (src: string) => {
      switch (adjustedUxMode) {
        case 'windoid':
          windoidRef.current = openWindoid(src, handleWindoidMessage);

          break;
        case 'redirect':
          isoWindow.location.href = src;
          break;
      }
    },
    [adjustedUxMode, handleWindoidMessage],
  );

  const handleButtonClick = useCallback(() => {
    trackUserAction({userAction: 'SIGN_IN_WITH_SHOP_BUTTON_CLICK'});

    openWindoidOrRedirect(authorizeUrl);
  }, [authorizeUrl, openWindoidOrRedirect, trackUserAction]);

  const debouncedButtonClick = useDebouncedCallback(
    handleButtonClick,
    150,
    true,
  );

  const handlePromptChange = useCallback(() => {
    trackUserAction({userAction: 'SIGN_IN_WITH_SHOP_PROMPT_CHANGE_CLICK'});
    iframeRef?.current?.close({
      dismissMethod: 'prompt_change',
      reason: 'user_prompt_change_clicked',
    });
  }, [trackUserAction]);

  const handlePromptContinue = useWindoidPreAuthPromptContinue({
    getAuthorizeUrl,
    getEmail: getSubmittedEmail,
    iframeRef,
    openWindoid: openWindoidOrRedirect,
  });

  const button =
    hideButton || activatorElementRef.current || !contentVisible ? null : (
      <BrandedButton
        bordered={bordered}
        fullWidth={fullWidth}
        onClick={debouncedButtonClick}
        ref={buttonRef}
      >
        {translate('shopLoginButton.continue', {
          shop: <BrandedButtonIcon />,
        })}
      </BrandedButton>
    );

  const isIframe = adjustedUxMode === 'iframe';
  const activator = activatorElementRef.current
    ? activatorElementRef
    : buttonRef;
  const iframeActivator = isIframe ? activator : undefined;
  const iframeSrc = useMemo(() => {
    return isIframe ? authorizeUrl : getAuthorizeUrl({uxRole: 'prompt'});
  }, [authorizeUrl, getAuthorizeUrl, isIframe]);

  const modalType = isIframe || isMobileBrowser() ? 'default' : 'wide';

  const shouldRenderIframe = useMemo(() => {
    const hasAnyInitScriptMemo = hasAnyInitScript();
    return (
      (adjustedUxMode !== 'windoid' && adjustedUxMode !== 'redirect') ||
      hasAnyInitScriptMemo
    );
  }, [adjustedUxMode]);
  return (
    <>
      {button}
      {/* Only render the AuthorizeIframe if not windoid,
      or if windoid and at least one init script is present */}
      {shouldRenderIframe && (
        <AuthorizeIframe
          activator={iframeActivator}
          anchorTo={anchorTo}
          autoOpen={autoOpen && !windoidRef.current}
          onComplete={handleComplete}
          onError={handleError}
          onPromptChange={handlePromptChange}
          onPromptContinue={handlePromptContinue}
          proxy={shouldProxy}
          ref={iframeRef}
          src={iframeSrc}
          modalType={modalType}
          variant="default"
        />
      )}
    </>
  );
};

const getFeatureDictionary = async (locale: string) =>
  // Temporary adding an await here so that the i18n-dynamic-import-replacer
  // replaces the import statement with the correct file.
  // https://github.com/Shopify/shop-identity/issues/2814
  // eslint-disable-next-line no-return-await
  await import(`./translations/${locale}.json`);

export const registerShopLoginButton = () =>
  register<ShopLoginButtonProps>(
    ({element, ...props}) => (
      <RootProvider
        element={element}
        featureName="ShopLoginButton"
        getFeatureDictionary={getFeatureDictionary}
        monorailProps={getAnalyticsProps(props)}
      >
        <ShopLoginButtonInternal {...props} />
      </RootProvider>
    ),
    {
      methods: ['requestShow', 'start', 'showToast'],
      name: 'shop-login-button',
      props: {
        action: 'string',
        activatorId: 'string',
        analyticsContext: 'string',
        anchorTo: 'string',
        apiKey: 'string',
        autoOpen: 'boolean',
        avoidPayAltDomain: 'boolean',
        bordered: 'boolean',
        checkoutVersion: 'string',
        clientId: 'string',
        codeChallenge: 'string',
        codeChallengeMethod: 'string',
        consentChallenge: 'string',
        disableSignUp: 'boolean',
        email: 'string',
        emailInputSelector: 'string',
        // experiments is a json stringified dictionary of experiments (key=handle, value=variant assigned)
        experiments: 'string',
        firstname: 'string',
        flow: 'string',
        flowVersion: 'string',
        fullWidth: 'boolean',
        hideButton: 'boolean',
        keepModalOpen: 'boolean',
        lastname: 'string',
        persistAfterSignIn: 'boolean',
        popUpFeatures: 'string',
        popUpName: 'string',
        proxy: 'boolean',
        redirectType: 'string',
        redirectUri: 'string',
        requireVerification: 'boolean',
        responseMode: 'string',
        responseType: 'string',
        returnUri: 'string',
        scope: 'string',
        source: 'string',
        state: 'string',
        storefrontDomain: 'string',
        uxMode: 'string',
        version: 'string',
      },
      shadow: 'open',
    },
  );

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shop-login-button']: ShopLoginButtonWebComponentProps;
    }
  }
}

export function ShopLoginButton(props: ShopLoginButtonWebComponentProps) {
  return <shop-login-button {...props} />;
}
